<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import NProgress from "nprogress";
import { useForm } from "@/form";
import { ResourceTable, ResourceTableBody, ResourceTableHeader, ResourceTableHeaders } from "@Components/Table";
import { PrimaryButton } from "@Components/Button";
import LinkTableRow from "./Partials/LinkTableRow.vue";
import CreateLinkModal from "./Partials/CreateLinkModal.vue";
import Layout from "./Partials/Layout.vue";

const { t: __ } = useI18n();
const router = useRouter();
const route = useRoute();

const form = useForm({
    sort_order: route.query?.sort_order,
    sort_direction: route.query?.sort_direction,
});

const createLinkModal = ref();
const processing = ref(false);
const links = ref();
const [postTypes] = await Promise.all([
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_post_types")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

// methods
const fetch = async () => {
    NProgress.start();
    processing.value = true;
    links.value = await form.post("linking_manual_index");
    processing.value = false;
    NProgress.done();
};

// computed
const sort = computed({
    get() {
        return {
            order: form.sort_order,
            direction: form.sort_direction,
        };
    },
    set(newValue) {
        form.sort_order = newValue.order;
        form.sort_direction = newValue.direction;
    },
});

// watchers
watch(
    () => form.data(),
    async () => {
        router.push({ name: "linking.manual.index", query: form.data() });
        await fetch();
    },
    { deep: true }
);
// ---

await fetch();
</script>

<template>
    <Layout>
        <template v-if="links.length > 0">
            <header class="flex justify-between items-center px-4 py-4 border-b border-gray-300 dark:bg-gray-900 dark:border-gray-800 lg:px-8 sm:px-6 sm:py-6">
                <div class="flex items-center">
                    <h1 class="text-base font-semibold leading-7 text-gray-900 dark:text-white">{{ __("Mes liens") }}</h1>
                </div>

                <PrimaryButton @click="createLinkModal.show()" :z="1"> {{ __("Créer un lien") }}</PrimaryButton>
            </header>

            <ResourceTable class="mt-6">
                <ResourceTableHeaders>
                    <ResourceTableHeader column="url" v-model="sort" :sortable="true" class="pl-4 lg:pl-8 sm:pl-6">{{ __("Lien") }}</ResourceTableHeader>
                    <ResourceTableHeader column="keywords">{{ __("Ancres") }}</ResourceTableHeader>
                    <ResourceTableHeader column="post_types">{{ __("Types de posts") }}</ResourceTableHeader>
                    <ResourceTableHeader column="max_links" v-model="sort" :sortable="true">{{ __("Liens maximum") }}</ResourceTableHeader>
                    <ResourceTableHeader>{{ __("Options du lien") }}</ResourceTableHeader>
                    <ResourceTableHeader class="w-1/12 pr-4 lg:pr-8 sm:pr-6">
                        <span class="sr-only">Edit</span>
                    </ResourceTableHeader>
                </ResourceTableHeaders>
                <ResourceTableBody>
                    <LinkTableRow v-for="link in links" :key="link.id" :postTypes="postTypes" :link="link" @deleted="fetch" @updated="fetch" />
                </ResourceTableBody>
            </ResourceTable>
        </template>
        <div v-else class="mx-auto max-w-xl py-16">
            <button
                @click="createLinkModal.show()"
                type="button"
                class="w-full relative block p-12 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-800 dark:focus:border-gray-400 dark:hover:border-gray-600 focus:border-gray-900 focus:outline-none hover:border-gray-500">
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun lien") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de lien.") }}</div>
                <PrimaryButton class="mt-8">{{ __("Créer un lien") }}</PrimaryButton>
            </button>
        </div>

        <CreateLinkModal ref="createLinkModal" @created="fetch" :postTypes="postTypes" />
    </Layout>
</template>
